import { Format, NumberFormatter } from '@zervant/reactcomponents'
import moment from 'moment'
import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import { navigateAddModal, navigateRemoveModal } from '../../../actions/navigation'
import { updateRecurringProfileSettings } from '../../../actions/recurringProfile'
import { DraftInterface } from '../../../Interfaces/Draft/DraftInterface'
import {
  DEFAULT_REPEAT_COUNT,
  RecurringActionType,
  RecurringDurationType,
  RecurringFrequencyType,
  RecurringMessageInterface,
  RecurringProfileSettingsInterface,
  RecurringProfileStatusType,
} from '../../../Interfaces/RecurringProfileInterface'
import { StateInterface } from '../../../Interfaces/StateInterface'
import { QuotaTypes, SubscriptionInterface } from '../../../Interfaces/Subscription'
import { transformDateFormatForMoment } from '../../../Other/functions/dateFunctions'
import { camelize } from '../../../Other/functions/validationFunctions'
import { Immutable } from '../../../Other/Immutable'
import { l } from '../../../Other/Translation'
import { doesCurrentPlanSupportFeature } from '../../../reducers/Subscription/companySubscription'
import Button, { ButtonType } from '../../common/Button/Button'
import CheckBox from '../../common/CheckBox/CheckBox'
import { DatePicker } from '../../common/DatePicker/DatePicker'
import { Document } from '../../common/Document/Document'
import { TestableInterface } from '../../common/interfaces/TestableInterface'
import { NumberInput } from '../../common/NumberInput'
import { SelectInput } from '../../common/SelectInput/SelectInput'
import RecurringEmailModal from '../../Modal/RecurringEmailModal'
import keys from '../../txtkeys/recurringProfileKeys'
import './RecurringProfileSettings.scss'

interface ExternalProps extends TestableInterface {
  draft: DraftInterface
  recurringProfile: RecurringProfileSettingsInterface
  dateFormat
  language: string
  currency: string
  locale: string
  subscription: SubscriptionInterface
  companyEmail: string
  userAccountEmail: string
}

interface ActionProps {
  updateRecurringProfile: (profile: RecurringProfileSettingsInterface) => void
  openMessageModal: (message: RecurringMessageInterface, profile: RecurringProfileSettingsInterface, dateFormat) => void
}

interface ExternalActionProps {
  changeFields: (fields) => void
}

interface OwnStateProps {
  menuOpen: boolean
  scheduleMap: any
  repeat: number
}

const START_DATE_FIELD = 'startDate'

type Props = ExternalProps & ActionProps & ExternalActionProps
export class RecurringProfileSettingsClass extends React.Component<Props, OwnStateProps> {
  constructor(props) {
    super(props)

    this.state = {
      menuOpen: false,
      scheduleMap: this.getScheduleMap(),
      repeat: DEFAULT_REPEAT_COUNT,
    }
  }

  private numberFormatter = new NumberFormatter({
    numbers: {
      isDecimal: false,
      decimalAccuracy: 0,
      decimalSeparator: '.',
      isGrouped: false,
      groupSeparator: ' ',
      groupSize: 999,
    },
    unit: null,
    currency: null,
    dates: null,
  })

  public componentDidMount() {
    if (!(typeof this.props?.recurringProfile?.startDate === 'string')) {
      this.onStartDateChange(this.props.draft.fields.issueDate.value)
    }
  }

  private hasPremiumAccess = () => {
    return doesCurrentPlanSupportFeature(this.props.subscription, QuotaTypes.RECURRING_PROFILE)
  }

  private getScheduleMap(date = null) {
    const map = {}
    const values = Object.keys(RecurringFrequencyType)
    values.forEach((v) => {
      if (v === RecurringFrequencyType.MONTHLY) {
        moment.locale(this.props.language)
        const startDate = date || this.props.recurringProfile.startDate
        const dayOfMonth = moment(startDate).format('Do')
        map[v] = `${l(keys.recurring.frequency[v])} (${dayOfMonth})`
      } else {
        map[v] = l(keys.recurring.frequency[v])
      }
    })
    return map
  }

  private updateScheduleMap(date = null) {
    this.setState({ scheduleMap: this.getScheduleMap(date) })
  }

  private getDurationMap() {
    const className = ['recurring-custom-value']
    if (!this.props.recurringProfile.repeat) {
      className.push('disabled')
    }
    return {
      [RecurringDurationType.INDEFINITE]: l(keys.recurring.duration.indefinite),
      [RecurringDurationType.REPEAT]: l(keys.recurring.duration.repeat),
    }
  }

  private getSendingMap() {
    const values = [RecurringActionType.CREATE_AND_SEND, RecurringActionType.CREATE]
    const map = {}
    values.forEach((v) => (map[v] = l(keys.recurring.sending[camelize(v)])))
    return map
  }

  private getSenderEmailAddress() {
    return this.props.draft.invoicer.tradeName.contact.email || this.props.companyEmail || this.props.userAccountEmail
  }

  private getConfirmationLabel() {
    const email = this.getSenderEmailAddress()
    return `${l(keys.recurring.confirmation.label)} (${email})`
  }

  private onStartDateChange(startDate) {
    const fields = Immutable.copy(this.props.draft.fields)
    fields.issueDate.value = startDate
    this.props.changeFields({ ...fields })
    // if schedule is "monthly, last day" and selected date is not last day of month, change schedule to "monthly"
    if (
      this.props.recurringProfile.frequency === RecurringFrequencyType.MONTHLY_LAST_DAY &&
      moment(startDate).endOf('day').isBefore(moment(startDate).endOf('month'))
    ) {
      this.props.updateRecurringProfile({
        ...this.props.recurringProfile,
        frequency: RecurringFrequencyType.MONTHLY,
        startDate,
      })
    } else {
      this.props.updateRecurringProfile({
        ...this.props.recurringProfile,
        startDate,
      })
    }
    this.updateScheduleMap(startDate)
  }

  private onFrequencyChange(frequency) {
    // if selected schedule is "monthly, last day", update start date and issueDate in draft to the last day of month at noon local time
    if (frequency === RecurringFrequencyType.MONTHLY_LAST_DAY) {
      const startDate = moment(this.props.draft.fields.issueDate.value)
        .endOf('month')
        .hours(12)
        .minutes(0)
        .seconds(0)
        .milliseconds(0)
        .toISOString()
      this.props.updateRecurringProfile({
        ...this.props.recurringProfile,
        frequency,
        startDate,
      })
      const fields = Immutable.copy(this.props.draft.fields)
      fields.issueDate.value = startDate
      this.props.changeFields({ ...fields })
    } else {
      this.props.updateRecurringProfile({
        ...this.props.recurringProfile,
        frequency,
      })
    }
  }

  private generateSubject = (): string => {
    const currencyF = new NumberFormatter(Format.fromLocale(this.props.locale), this.props.currency)
    const invoice = `${l(keys.recurring.invoice)} ${this.props.draft.fields.invoiceNumber.value}`
    const name = this.props.draft.receiver ? this.props.draft.receiver.displayName : ''
    const total = currencyF.render(this.props.draft.summary.grossTotal)
    return `${invoice} - ${name} - ${total}`
  }

  private generateBody = (): string => {
    const messageBody = l(keys.recurring.email)
    const sender = this.props.draft.invoicer.tradeName.name
    return `${messageBody.replace(/<br>/g, '\n')}\n\n${sender}`
  }

  private getEmail = () => {
    return {
      to: this.props.draft.receiver ? this.props.draft.receiver.email : null,
      sender: this.getSenderEmailAddress(),
      subject: this.generateSubject(),
      body: this.props.recurringProfile.message.body || this.generateBody(),
      acceptStripePayment: true,
    }
  }

  public render(): JSX.Element {
    const automation = this.props.automation
    const { recurringProfile } = this.props
    const now = moment(moment().toISOString())
    const approvedStartDate = moment(recurringProfile.startDate)
    const isActive = Boolean(recurringProfile.status === RecurringProfileStatusType.ACTIVE)
    const isPickingStartDateDisabled = recurringProfile.id && isActive ? approvedStartDate.isSameOrBefore(now) : false
    const className = this.hasPremiumAccess()
      ? 'RecurringProfileSettings'
      : 'RecurringProfileSettings RecurringProfileSettings--disabled'
    return (
      <Document automation={`${automation}`} className={className}>
        <h1 className="title">{l(keys.recurring.blockLabel)}</h1>
        <div className={'row'}>
          <div className={'column'}>
            <DatePicker
              value={recurringProfile.startDate}
              automation={`${automation}-date`}
              labelPosition={'top'}
              label={l(keys.recurring.starting)}
              format={this.props.dateFormat}
              onChange={(startDate) => this.onStartDateChange(startDate)}
              disabled={isPickingStartDateDisabled || isActive}
              minDate={moment()}
              locale={this.props.locale}
              invalid={this.props.draft.invalidFields && this.props.draft.invalidFields.indexOf(START_DATE_FIELD) >= 0}
            />
          </div>
          <div className={'column'}>
            <SelectInput
              value={recurringProfile.frequency}
              automation={`${automation}-schedule`}
              map={this.state.scheduleMap}
              label={l(keys.recurring.frequency.header)}
              labelPosition={'top'}
              onChange={(schedule: RecurringFrequencyType) => this.onFrequencyChange(schedule)}
            />
          </div>
          <div className={'column'}>
            <SelectInput
              value={recurringProfile.repeat ? RecurringDurationType.REPEAT : RecurringDurationType.INDEFINITE}
              automation={`${automation}-duration`}
              map={this.getDurationMap()}
              label={l(keys.recurring.duration.header)}
              labelPosition={'top'}
              onChange={(duration: RecurringDurationType) =>
                this.props.updateRecurringProfile({
                  ...recurringProfile,
                  repeat: duration === RecurringDurationType.INDEFINITE ? null : this.state.repeat,
                })
              }
            />
          </div>
          {recurringProfile.repeat && (
            <div className={'column custom'}>
              <NumberInput
                automation={`${automation}-duration-custom-value`}
                className={['recurring-duration-custom-value']}
                value={recurringProfile.repeat}
                formatter={this.numberFormatter}
                onBlur={(repeat) => {
                  if (repeat) {
                    this.props.updateRecurringProfile({
                      ...recurringProfile,
                      repeat,
                    })
                    this.setState({ repeat })
                  } else {
                    this.props.updateRecurringProfile({
                      ...recurringProfile,
                      repeat: this.state.repeat,
                    })
                  }
                }}
              />
              <span className={'recurring-custom-label'}>{l(keys.recurring.duration.times)}</span>
            </div>
          )}
        </div>
        <div className={'row'}>
          <div className={'column'}>
            <SelectInput
              automation={`${automation}-action`}
              value={recurringProfile.action}
              map={this.getSendingMap()}
              label={l(keys.recurring.sending.header)}
              labelPosition={'top'}
              onChange={(action: RecurringActionType) =>
                this.props.updateRecurringProfile({
                  ...recurringProfile,
                  action,
                })
              }
            />
            {recurringProfile.action === RecurringActionType.CREATE_AND_SEND && (
              <Button
                automation={`${automation}-edit-message`}
                type={ButtonType.link}
                label={l(keys.recurring.editMessage)}
                action={() =>
                  this.props.openMessageModal(this.getEmail(), this.props.recurringProfile, this.props.dateFormat)
                }
              />
            )}
          </div>
          <div className={'column confirmation'}>
            <CheckBox
              label={this.getConfirmationLabel()}
              value={recurringProfile.confirmation}
              automation={`${this.props.automation}-confirmation`}
              onChange={(confirmation: boolean) =>
                this.props.updateRecurringProfile({
                  ...recurringProfile,
                  confirmation,
                })
              }
            />
          </div>
        </div>
        {!this.hasPremiumAccess() ? (
          <div className={'help'}>
            <img className={'star'} src={'/images/premiumfeature/star_with_circle_green.svg'} />
            <span className={'helpText'}>{`${l(keys.recurring.help.text)} `}</span>
            <Button
              automation={`${automation}-read-more-button`}
              type={ButtonType.link}
              label={l(keys.recurring.help.button)}
            />
          </div>
        ) : (
          <div />
        )}
      </Document>
    )
  }
}

const buildSummary = (profile: RecurringProfileSettingsInterface, dateFormat): string => {
  const frequency = l(keys.recurring.frequency[profile.frequency])
  const times = profile.repeat ? `${profile.repeat} ${l(keys.recurring.duration.times)}, ` : ''
  const dateStr = moment(profile.startDate).format(dateFormat)
  const starting = `${l(keys.recurring.starting)} ${dateStr}`
  return `${l(keys.recurring.repeated)} ${frequency}, ${times} ${starting}`
}

const mapStateToProps = (state: StateInterface): ExternalProps => {
  return {
    subscription: state.subscriptionManagement.companySubscription.subscription,
    dateFormat: transformDateFormatForMoment(state.settings?.values?.regional?.longDate),
    draft: state.draft,
    recurringProfile: state.recurringProfile,
    language: state.token.locale.split('_')[0],
    automation: 'recurringProfileBlock',
    currency: state.draft.fields.currency.value,
    locale: state.token.locale,
    companyEmail: state.company.email,
    userAccountEmail: state.user.email,
  }
}
const mapDispatchToProps = (dispatch: Dispatch<any>): ActionProps => ({
  updateRecurringProfile: (profile) => dispatch(updateRecurringProfileSettings(profile)),
  openMessageModal: (email, recurringProfile, dateFormat) =>
    dispatch(
      navigateAddModal({
        component: 'RecurringEmailModal',
        props: {
          email,
          summary: buildSummary(recurringProfile, dateFormat),
          close: () => dispatch(navigateRemoveModal('RecurringEmailModal')),
          save: (message) => {
            dispatch(
              updateRecurringProfileSettings({
                ...recurringProfile,
                message: {
                  to: message.to,
                  body: message.body,
                  acceptStripePayment: message.acceptStripePayment,
                },
              }),
            )
            dispatch(navigateRemoveModal('RecurringEmailModal'))
          },
        },
      }),
    ),
})
export const RecurringProfileSettings = connect<ExternalProps, ActionProps, ExternalActionProps>(
  mapStateToProps,
  mapDispatchToProps,
)(RecurringProfileSettingsClass)
